// UC61-01/02 — the banner that names the data scope the session's observer role reads under.
//
// It renders for EVERY observer role, not only the restricted ones. A Specific Observer bound to
// compensated medicines and an unrestricted one differ by one sentence, and the version that
// showed the banner to the first and nothing to the second gave two sibling roles differently
// shaped pages: CNAM read its title 56px lower than MS on the same screen. The scope is also the
// thing an observer most needs stated, and "you see everything" is a statement, not an absence.
//
// Placement is the page's, not the shell's: it is rendered directly under each observer screen's
// page head, so the title stays where every other role's title is and the banner explains the
// data below it rather than pushing the heading down.
import { useTranslate } from '@tolgee/react';
import { MudInfoBox } from '@egov-moldova/mud-react';
import { useObserverScope } from '../../features/observer/use-observer-scope';
import { registrarKindLabel } from '../../features/registrars/kind-label';
import { useSession } from '../../hooks/use-session';

// CF-04 (2026-10-12): the tag and Registrar-type restrictions of a Specific Observer apply to
// the role's own dashboards only. UC91 and every other stock read give it exactly what the
// general Observer sees, so only the dashboards name the restriction.
export type ObserverScopeContext = 'stock' | 'dashboards';

export function ObserverScopeBanner({
  context = 'stock',
}: {
  context?: ObserverScopeContext;
}): JSX.Element | null {
  const { t } = useTranslate();
  const session = useSession();
  const role = session.data?.activeRole;
  const isObserver = Boolean(role && role.startsWith('OBSERVER'));
  const { scope } = useObserverScope(role);
  if (!isObserver || !scope) {
    return null;
  }

  if (scope.noAccess) {
    return (
      <div className="simsm-observer-banner">
        <MudInfoBox emphasis="strong" variant="warning">
          <span className="simsm-observer-banner__text">
            <strong>{role}</strong>
            <span>{t('observer_scope.no_access')}</span>
          </span>
        </MudInfoBox>
      </div>
    );
  }

  // An unrestricted role has no tag list to print, so its sentence is the reach itself
  // (caiet UC61-01: the Observer reads the stock of every Registrar).
  const detail =
    !scope.scoped || context === 'stock'
      ? t('observer_scope.national')
      : scope.description ||
        t('observer_scope.detail', {
          scope: [
            ...scope.medicineTags,
            ...scope.registrarKinds.map((kind) => registrarKindLabel(t, kind)),
          ].join(', '),
        });

  return (
    <div className="simsm-observer-banner">
      <MudInfoBox emphasis="strong" variant="info">
        <span className="simsm-observer-banner__text">
          <strong>{scope.displayName || scope.roleCode}</strong>
          <span>{detail}</span>
        </span>
      </MudInfoBox>
    </div>
  );
}
